<template>
  <div>
    <h1 class="mb-4">
      {{
        $t('usersTitle', {
          platform: this.$config.hostName
        })
      }}
    </h1>
    <b-row>
      <b-col lg="4" md="6" class="ml-auto">
        <b-form-group class="mb-4">
          <b-input-group>
            <b-form-input v-model="filter" :placeholder="$t('search')" />
            <b-input-group-append>
              <b-button
                variant="primary"
                :disabled="!filter"
                @click="filter = ''"
              >
                {{ $t('clear') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>
    <b-table
      responsive
      id="users"
      outlined
      :items="users"
      :fields="fields"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="filter"
      @filtered="onFiltered"
      show-empty
    >
      <template slot="name" slot-scope="data">
        <router-link
          :to="{
            name: 'hunter-profile-admin',
            params: { userId: data.item.id }
          }"
        >
          {{ data.item.name }}
        </router-link>
      </template>
      <template slot="verified" slot-scope="data">
        <b-badge
          :variant="getVerifiedStatusClass(data.item.verified)"
          class="text-uppercase"
        >
          {{ $t(`statuses.${getVerifiedStatus(data.item.verified)}`) }}
        </b-badge>
      </template>
      <template slot="status" slot-scope="data">
        <b-badge
          :variant="getUserStatusClass(data.item.isBanned)"
          class="text-uppercase"
        >
          {{ getUserStatus(data.item.isBanned) }}
        </b-badge>
      </template>
      <template slot="roles" slot-scope="data">
        <b-badge
          :key="index"
          variant="primary"
          v-for="(role, index) in getFilteredRoles(data.item.roles)"
          class="text-uppercase"
        >
          {{ getRoleName(role) }}
        </b-badge>
      </template>
      <template slot="empty">
        <div class="text-center">
          {{ $t('noRecords') }}
        </div>
      </template>
    </b-table>
    <b-row class="users-bottom flex-wrap">
      <b-col sm="6" class="d-flex d-sm-block justify-content-center">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          aria-controls="users"
        />
      </b-col>
      <b-col sm="6" class="d-flex d-sm-block justify-content-center">
        <div class="d-flex flex-row-reverse">
          <b-form-group
            label-cols="5"
            label-class="text-nowrap"
            :label="$t('perPage')"
            class="mb-0 ml-auto"
          >
            <b-form-select
              v-model="perPage"
              :options="pageOptions"
              style="width: 65px"
            />
          </b-form-group>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import * as auth from '@/utils/auth.js';

export default {
  data() {
    return {
      currentPage: 1,
      users: [],
      fields: [
        {
          key: 'name',
          label: this.$t('name'),
          sortable: true,
          tdClass: 'pl-5 pr-5'
        },
        {
          key: 'verified',
          label: this.$t('verified'),
          sortable: true,
          tdClass: 'pl-5 pr-5'
        },
        {
          key: 'status',
          label: this.$tc('status', 1),
          sortable: true,
          tdClass: 'pl-5 pr-5'
        },
        {
          key: 'roles',
          label: this.$t('roles'),
          sortable: true,
          tdClass: 'pl-5 pr-5'
        }
      ],
      verified: 'verified',
      filter: null,
      perPage: 20,
      pageOptions: [5, 10, 20, 30, 50],
      totalRows: 1
    };
  },

  computed: {
    isAdmin() {
      return auth.readActiveRole() === 'admin';
    }
  },

  methods: {
    getFilteredRoles(roles) {
      return roles.filter(role => role !== 7 && this.getRoleName(role));
    },

    getRoleName(id) {
      if (id === 7) {
        return null;
      }
      return this.prettify(this.$config.roles.get(id));
    },

    prettify(input) {
      if (!input) {
        return '';
      }
      let result = '';
      let words = input.split('-');
      words.forEach((word, index) => {
        result += word.charAt(0).toUpperCase() + word.slice(1);
        if (index !== words.length - 1) {
          result += ' ';
        }
      });

      return result;
    },

    getVerifiedStatus(isVerified) {
      return isVerified ? 'verified' : 'unverified';
    },

    getVerifiedStatusClass(isVerified) {
      return isVerified ? 'success' : 'warning';
    },

    getUserStatus(data) {
      return data ? this.$t(`statuses.banned`) : this.$t(`statuses.active`);
    },

    getUserStatusClass(data) {
      return data ? 'danger' : 'success';
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  },

  created() {
    this.$api.auth.get(`/users`).then(
      data => {
        if (data.users) {
          data.users.forEach(user => {
            let item = {
              id: user.id,
              name: `${user.firstName} ${user.lastName}`,
              verified: user.roles.indexOf(7) > -1,
              isBanned: user.isBanned,
              banDescription: user.banDescription,
              roles: user.roles
            };
            if (
              (!item.roles.includes(8) && !item.roles.includes(9)) ||
              this.isAdmin
            ) {
              this.users.push(item);
            }
          });
          this.totalRows = this.users.length;
        }
      },
      e => {
        e.response.data &&
          this.$notify('alert', {
            text: e.response.data.error.message,
            variant: 'danger',
            time: 5000
          });
      }
    );
  }
};
</script>

<style lang="scss">
.users-bottom {
  legend {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}
@include media-breakpoint-down(xs) {
  .users-bottom {
    legend {
      justify-content: start;
    }
  }
}
</style>
